import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { usePlayer } from "@/context/PlayerContext";
import { ChevronLeft, Save, Loader2, Play, Pause, Trash2, RefreshCw, Plus, GripVertical, Calendar as CalendarIcon } from "lucide-react";

const CATEGORIES = ["Commercial", "Animation", "Character", "Narration", "E-learning", "Gaming"];

// Standard gender-based profile photos (self-hosted in /public)
const STANDARD_PHOTO = { male: "/profile-boy.png", female: "/profile-girl.png" };
const standardPhotoFor = (gender) => STANDARD_PHOTO[gender] || STANDARD_PHOTO.male;

// VoiceOverKids Tags (multi-select)
const VOK_TAGS = [
  "Cheerful & spontaneous",
  "Sweet & soft",
  "Cool & energetic",
  "Curious & cheeky",
  "Fresh & natural",
  "Cute & playful",
  "Calm & clear",
  "Expressive & enthusiastic",
  "Dry & funny",
  "Warm & sincere",
];

export default function AdminTalentEdit() {
  const { id } = useParams();
  const player = usePlayer();
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/talents/${id}`).then((r) => setT(r.data)).catch(() => toast.error("Not found"));
  useEffect(() => { load(); }, [id]);

  if (!t) return <div className="flex justify-center py-20 text-brand-violet"><Loader2 className="animate-spin" /></div>;

  const set = (k, v) => setT((p) => ({ ...p, [k]: v }));

  // Gender change auto-assigns the matching standard photo
  const setGender = (v) => setT((p) => ({ ...p, gender: v, photo_url: standardPhotoFor(v) }));

  const rawTags = Array.isArray(t.tags) ? t.tags : String(t.tags || "").split(",").map((s) => s.trim()).filter(Boolean);
  const selectedTags = rawTags.filter((x) => VOK_TAGS.includes(x));
  const toggleTag = (tag) => {
    const has = selectedTags.includes(tag);
    set("tags", has ? selectedTags.filter((x) => x !== tag) : [...selectedTags, tag]);
  };

  const displayPhoto = standardPhotoFor(t.gender);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...t, tags: selectedTags, photo_url: standardPhotoFor(t.gender) };
      await api.put(`/admin/talents/${id}`, payload);
      toast.success("Saved");
      load();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <Link to="/admin/talents" className="inline-flex items-center gap-1 text-sm font-semibold text-zinc-500 hover:text-brand-violet"><ChevronLeft size={16} /> Back to voices</Link>
      <div className="mt-3 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">{t.name}</h1>
        <button onClick={save} disabled={saving} data-testid="save-talent" className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-6 py-2.5 text-sm font-bold text-white hover:bg-brand-violetHover disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
        </button>
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-[220px_1fr]">
        <div>
          <div className="aspect-square overflow-hidden rounded-2xl border border-zinc-200 bg-zinc-100" data-testid="standard-photo-preview">
            <img src={mediaUrl(displayPhoto)} alt="" className="h-full w-full object-cover" />
          </div>
          <p className="mt-2 text-center text-xs text-zinc-400">Standard {t.gender === "female" ? "girl" : "boy"} photo — set automatically by gender</p>
          <div className="mt-4 space-y-3 rounded-xl border border-zinc-200 bg-white p-4">
            <ToggleRow label="Published" val={t.published} onChange={(v) => set("published", v)} testid="edit-published" />
            <ToggleRow label="Featured" val={t.featured} onChange={(v) => set("featured", v)} testid="edit-featured" />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Public name"><Input value={t.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-name" className="rounded-xl h-11" /></F>
          <F label="Voice type">
            <Select value={t.voice_type} onValueChange={(v) => set("voice_type", v)}>
              <SelectTrigger className="rounded-xl h-11"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="human">Human</SelectItem><SelectItem value="ai">AI</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Language"><Input value={t.language || ""} onChange={(e) => set("language", e.target.value)} className="rounded-xl h-11" /></F>
          <F label="Country"><Input value={t.country || ""} onChange={(e) => set("country", e.target.value)} className="rounded-xl h-11" /></F>
          <F label="Accent"><Input value={t.accent || ""} onChange={(e) => set("accent", e.target.value)} className="rounded-xl h-11" /></F>
          <F label="Gender">
            <Select value={t.gender} onValueChange={setGender}>
              <SelectTrigger className="rounded-xl h-11" data-testid="edit-gender"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="male">Boy</SelectItem><SelectItem value="female">Girl</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Age min"><Input type="number" value={t.age_min ?? ""} onChange={(e) => set("age_min", e.target.value ? parseInt(e.target.value) : null)} className="rounded-xl h-11" /></F>
          <F label="Age max"><Input type="number" value={t.age_max ?? ""} onChange={(e) => set("age_max", e.target.value ? parseInt(e.target.value) : null)} className="rounded-xl h-11" /></F>
          <div className="sm:col-span-2">
            <Label className="label-eyebrow text-zinc-500">VoiceOverKids Tags</Label>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="vok-tags">
              {VOK_TAGS.map((tag) => {
                const active = selectedTags.includes(tag);
                return (
                  <label key={tag} data-testid={`vok-tag-${tag}`}
                    className={`flex cursor-pointer items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm font-semibold transition-colors ${active ? "border-brand-violet bg-violet-50 text-brand-violet" : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-violet"}`}>
                    <input type="checkbox" checked={active} onChange={() => toggleTag(tag)} className="h-4 w-4 accent-[var(--brand-violet,#7c3aed)]" />
                    {tag}
                  </label>
                );
              })}
            </div>
          </div>
          <div className="sm:col-span-2"><F label="Description"><Textarea rows={3} value={t.description || ""} onChange={(e) => set("description", e.target.value)} className="rounded-xl" /></F></div>
        </div>
      </div>

      <DemoManager talent={t} reload={load} player={player} />
    </div>
  );
}

function F({ label, children }) { return <div className="space-y-1.5"><Label className="label-eyebrow text-zinc-500">{label}</Label>{children}</div>; }
function ToggleRow({ label, val, onChange, testid }) {
  return <div className="flex items-center justify-between"><span className="text-sm font-semibold text-zinc-700">{label}</span><Switch checked={!!val} onCheckedChange={onChange} data-testid={testid} /></div>;
}

function DemoManager({ talent, reload, player }) {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);
  const fileRefs = useRef({});
  const newRef = useRef();
  const [newTitle, setNewTitle] = useState("");
  const [newCat, setNewCat] = useState("Commercial");

  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "demos");
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const addDemo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setAdding(true);
    try {
      const url = await uploadFile(file);
      await api.post(`/admin/talents/${talent.id}/demos`, { title: newTitle || file.name.replace(/\.[^.]+$/, ""), category: newCat, url, language: talent.language });
      toast.success("Demo added"); setNewTitle(""); reload();
    } catch (err) { toast.error("Upload failed"); } finally { setAdding(false); e.target.value = ""; }
  };

  const replaceDemo = async (demoId, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(demoId);
    try {
      const url = await uploadFile(file); // new unique object -> avoids CDN cache
      await api.put(`/admin/talents/${talent.id}/demos/${demoId}`, { url });
      toast.success("Audio replaced"); reload();
    } catch (err) { toast.error("Replace failed"); } finally { setBusy(null); e.target.value = ""; }
  };

  const updateDemo = async (demoId, fields) => { await api.put(`/admin/talents/${talent.id}/demos/${demoId}`, fields); reload(); };
  const delDemo = async (demoId) => { if (!window.confirm("Delete this demo?")) return; await api.delete(`/admin/talents/${talent.id}/demos/${demoId}`); reload(); };

  return (
    <div className="mt-10">
      <h2 className="font-heading text-lg font-bold">Audio demos</h2>
      <div className="mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-dashed border-zinc-300 bg-white p-4">
        <div className="flex-1 min-w-[160px]"><Label className="label-eyebrow text-zinc-500">New demo title</Label><Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. Commercial demo" className="rounded-xl h-10 mt-1" /></div>
        <div><Label className="label-eyebrow text-zinc-500">Category</Label>
          <Select value={newCat} onValueChange={setNewCat}><SelectTrigger className="rounded-xl h-10 mt-1 w-40"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </div>
        <input ref={newRef} type="file" accept="audio/*" hidden onChange={addDemo} data-testid="add-demo-input" />
        <button onClick={() => newRef.current?.click()} disabled={adding} data-testid="add-demo-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-violetHover disabled:opacity-60">
          {adding ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Upload demo
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {(talent.demos || []).map((d) => {
          const key = `${talent.id}:${d.id}`;
          const active = player.isActive(key);
          return (
            <div key={d.id} data-testid={`admin-demo-${d.id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-zinc-200 bg-white p-3">
              <GripVertical size={16} className="text-zinc-300" />
              <button onClick={() => player.playTrack({ key, name: talent.name, sub: d.title, url: mediaUrl(d.url), photo: mediaUrl(talent.photo_url), talentId: talent.id })}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-violet text-white">
                {active && player.playing ? <Pause size={15} className="fill-white" /> : <Play size={15} className="fill-white ml-0.5" />}
              </button>
              <Input value={d.title} onChange={(e) => { d.title = e.target.value; }} onBlur={(e) => updateDemo(d.id, { title: e.target.value })} className="h-9 w-44 rounded-lg" />
              <Select value={d.category} onValueChange={(v) => updateDemo(d.id, { category: v })}><SelectTrigger className="h-9 w-36 rounded-lg"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              <label className="flex items-center gap-1.5 text-xs text-zinc-600"><Switch checked={d.published} onCheckedChange={(v) => updateDemo(d.id, { published: v })} /> Published</label>
              <DemoDatePicker value={d.uploaded_at} onChange={(iso) => updateDemo(d.id, { uploaded_at: iso })} testid={`demo-date-${d.id}`} />
              <div className="ml-auto flex items-center gap-1">
                <input ref={(el) => (fileRefs.current[d.id] = el)} type="file" accept="audio/*" hidden onChange={(e) => replaceDemo(d.id, e)} data-testid={`replace-input-${d.id}`} />
                <button onClick={() => fileRefs.current[d.id]?.click()} disabled={busy === d.id} data-testid={`replace-demo-${d.id}`} className="inline-flex items-center gap-1 rounded-lg border border-zinc-300 px-3 py-1.5 text-xs font-semibold hover:border-brand-violet">
                  {busy === d.id ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Replace
                </button>
                <button onClick={() => delDemo(d.id)} className="flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
          );
        })}
        {(talent.demos || []).length === 0 && <p className="text-sm text-zinc-400">No demos yet. Upload the first one above.</p>}
      </div>
    </div>
  );
}

function DemoDatePicker({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const date = value ? new Date(value) : undefined;
  const valid = date && !isNaN(date);
  const label = valid ? format(date, "dd MMM yyyy") : "Set date";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:border-brand-violet">
          <CalendarIcon size={13} /> {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={valid ? date : undefined}
          defaultMonth={valid ? date : new Date()}
          onSelect={(d) => { if (d) { const x = new Date(d); x.setHours(12, 0, 0, 0); onChange(x.toISOString()); setOpen(false); } }}
          captionLayout="dropdown-buttons"
          fromYear={2010}
          toYear={new Date().getFullYear() + 1}
          classNames={{ caption_dropdowns: "flex gap-2 items-center", dropdown: "rounded-md border border-zinc-200 bg-white px-2 py-1 text-sm font-semibold", vhidden: "hidden", caption_label: "hidden" }}
        />
      </PopoverContent>
    </Popover>
  );
}
